import type { ReactElement } from "react";
import { useId } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { DropdownButton } from "@similarweb/ui-components";

import HooksModalV2Key from "app/constants/HooksModalV2Key";
import { CountrySelectCombobox } from "app/pages/sales-intelligence/common-components/dropdown/CountrySelectCombobox";
import { FormFieldLabel, RangeSelectWithFilter } from "app/pages/sales-intelligence/common-components/form-fields";
import FilterClearButton from "app/pages/sales-intelligence/modules/advanced-search-filtering/components/FilterClearButton";
import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import { StyledBaseDropdownButtonWrap } from "app/pages/sales-intelligence/sub-modules/advanced-search/components/common-styles";
import type { ICountryObject } from "app/services/CountryService";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";
import { openHooksModalV5 } from "app/services/ModalService";

import { COUNTRY_SELECT_WIDTH } from "../../../constants";
import type { IBaseDownloadEngagementFilter } from "../../../models/DownloadEngagementFilter/types";
import type { DeviceOption, IAppsDownloadsAndEngagementFilter } from "../../../types/commonFiltersDto";
import { formatDisplayedValue } from "../../../utils";
import DeviceDropdown from "../../DeviceDropdown/DeviceDropdown";

import { RANGE_SELECT_WIDTH } from "./const";
import { StyledContentFilter, StyledDDContainer } from "./styles";

type RangeValueType = number | undefined;

type DownloadEngagementFilterProps<
    TFilter extends IBaseDownloadEngagementFilter<TValue, unknown>,
    TValue extends IAppsDownloadsAndEngagementFilter,
> = IFilterViewBaseProps<TFilter> & {
    hasDeviceFilter?: boolean;
};

const DownloadEngagementFilter = <
    TFilter extends IBaseDownloadEngagementFilter<TValue, unknown>,
    TValue extends IAppsDownloadsAndEngagementFilter,
>({
    filter,
    value,
    onFilterUpdate,
    hasDeviceFilter = false,
}: DownloadEngagementFilterProps<TFilter, TValue>): ReactElement => {
    const { translate } = useTranslate();
    const dropdownId = useId();

    const renderDDButton = () => {
        return (
            <StyledBaseDropdownButtonWrap key="countries-dropdown-button">
                <DropdownButton>{translate("si.components.countries-dropdown.button.text")}</DropdownButton>
            </StyledBaseDropdownButtonWrap>
        );
    };

    const handleSelect = (country: ICountryObject) => {
        onFilterUpdate({ ...value, country: country.id } as TValue);
    };

    const handleMauChange = ([minValue, maxValue]: RangeValueType[]) => {
        onFilterUpdate({
            ...value,
            monthlyActiveUsers: {
                from: minValue,
                to: maxValue,
            },
        } as TValue);
    };

    const handleMonthlyDownloadsChange = ([minValue, maxValue]: RangeValueType[]) => {
        onFilterUpdate({
            ...value,
            monthlyDownloads: {
                from: minValue,
                to: maxValue,
            },
        } as TValue);
    };

    const clearMauFilters = () => {
        onFilterUpdate({
            ...value,
            monthlyActiveUsers: undefined,
        } as TValue);
    };

    const clearMonthlyDownloadsFilters = () => {
        onFilterUpdate({
            ...value,
            monthlyDownloads: undefined,
        } as TValue);
    };

    const handleDeviceChange = (item: DeviceOption) => {
        onFilterUpdate({
            ...value,
            device: item,
        } as TValue);
    };

    return (
        <StyledContentFilter>
            <FormFieldLabel text={translate("si.find_apps.filters.download-and-engagement.country.label")}>
                <StyledDDContainer
                    id={dropdownId}
                    customWidth={COUNTRY_SELECT_WIDTH}
                >
                    <CountrySelectCombobox
                        onSelect={handleSelect}
                        onRestrictedClick={() => {
                            openHooksModalV5(HooksModalV2Key.WebCountry);
                            return true;
                        }}
                        selectedIds={[value?.country || WORLDWIDE_COUNTRY_ID]}
                        items={filter.countries}
                        renderButton={renderDDButton}
                        searchPlaceHolder={translate("si.find_apps.filters.download-and-engagement.country.placeholder")}
                    />
                </StyledDDContainer>
            </FormFieldLabel>

            {hasDeviceFilter ? (
                <FormFieldLabel text={translate("si.find_apps.filters.download-and-engagement.devices.label")}>
                    <DeviceDropdown
                        options={filter.devices || []}
                        value={value?.device}
                        onChange={handleDeviceChange}
                    />
                </FormFieldLabel>
            ) : null}

            <FormFieldLabel text={translate("si.find_apps.filters.download-and-engagement.downloads.range.label")}>
                <RangeSelectWithFilter
                    fieldName="monthlyDownloads"
                    value={[value?.monthlyDownloads?.from, value?.monthlyDownloads?.to]}
                    optionsMin={filter.minDownloadsOptions}
                    optionsMax={filter.maxDownloadsOptions}
                    placeholderMin={translate("si.find_apps.filters.range.min.placeholder")}
                    placeholderMax={translate("si.find_apps.filters.range.max.placeholder")}
                    onChange={handleMonthlyDownloadsChange}
                    formatValue={formatDisplayedValue}
                    width={RANGE_SELECT_WIDTH}
                >
                    <FilterClearButton
                        isDisabled={!value?.monthlyDownloads}
                        onClick={clearMonthlyDownloadsFilters}
                    />
                </RangeSelectWithFilter>
            </FormFieldLabel>
            <FormFieldLabel text={translate("si.find_apps.filters.download-and-engagement.mau.range.label")}>
                <RangeSelectWithFilter
                    fieldName="monthlyActiveUsers"
                    value={[value?.monthlyActiveUsers?.from, value?.monthlyActiveUsers?.to]}
                    optionsMin={filter.minMauOptions}
                    optionsMax={filter.maxMauOptions}
                    placeholderMin={translate("si.find_apps.filters.range.min.placeholder")}
                    placeholderMax={translate("si.find_apps.filters.range.max.placeholder")}
                    onChange={handleMauChange}
                    formatValue={formatDisplayedValue}
                    width={RANGE_SELECT_WIDTH}
                >
                    <FilterClearButton
                        isDisabled={!value?.monthlyActiveUsers}
                        onClick={clearMauFilters}
                    />
                </RangeSelectWithFilter>
            </FormFieldLabel>
        </StyledContentFilter>
    );
};

export default DownloadEngagementFilter;
